import {computed, ref, watch} from 'vue'
import {useI18n} from 'vue-i18n'
import ApiService from '@/services/api.ts'
import {ErrorToast, SuccessToast, WarningToast} from '@/utils/popup'
import YarismaService from '@/services/yarisma'

export type ConnectionStatus = 'OFFLINE' | 'POOR' | 'GOOD'
/* Ai tarafından yapıldı detaylı test edilmeli */
const STATUS_ORDER: Record<ConnectionStatus, number> = { GOOD: 2, POOR: 1, OFFLINE: 0 }
const THRESHOLD_GOOD = 1000
const THRESHOLD_POOR = 2500
const CHECK_INTERVAL = 30000
const RECOVERY_DELAY = 5000
const TOAST_TIME_THRESHOLD = 1800
const TOAST_COOLDOWN_PERIOD = 60000

export function useBaglantiHizi() {
  const { t } = useI18n()

  const currentStatus = ref<ConnectionStatus>('GOOD')
  const connectionSpeed = ref(0)
  const isMonitoring = ref(false)
  const networkInfo = ref<{ effectiveType?: string; downlink?: number }>({})

  let intervalId: ReturnType<typeof setInterval> | null = null
  let recoveryTimeoutId: ReturnType<typeof setTimeout> | null = null
  let lastNotifiedStatus: ConnectionStatus = 'GOOD'
  let lastToastTime: number = 0

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getNetworkConnection = () => (navigator as any).connection

  const updateNetworkInfo = () => {
    const connection = getNetworkConnection()
    if (connection) {
      networkInfo.value = {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
      }
    }
  }

  const checkConnection = async () => {
    if (!navigator.onLine) {
      currentStatus.value = 'OFFLINE'
      return
    }

    updateNetworkInfo()
    const startTime = performance.now()

    try {
      const res = await fetch(`${ApiService.baseUrl}ping`)
      if (res.status !== 200) {
        currentStatus.value = 'OFFLINE'
        return
      }
    } catch {
      currentStatus.value = 'OFFLINE'
      return
    }

    const duration = Math.round(performance.now() - startTime)
    connectionSpeed.value = duration

    if (duration >= THRESHOLD_POOR) {
      currentStatus.value = 'OFFLINE'
    } else if (duration >= THRESHOLD_GOOD) {
      currentStatus.value = 'POOR'
    } else {
      currentStatus.value = 'GOOD'
    }
  }

  const handleOffline = () => {
    currentStatus.value = 'OFFLINE'
  }

  const handleOnline = async () => {
    await checkConnection()
  }

  const shouldShowToast = (): boolean => {
    const kalanSure = YarismaService.getBaslamasinaKalanSure()
    return kalanSure === null || kalanSure < TOAST_TIME_THRESHOLD
  }

  const isWorsening = (oldS: ConnectionStatus, newS: ConnectionStatus) =>
    STATUS_ORDER[newS] < STATUS_ORDER[oldS]

  const isRecovery = (oldS: ConnectionStatus, newS: ConnectionStatus) =>
    STATUS_ORDER[newS] > STATUS_ORDER[oldS]

  const clearRecoveryTimeout = () => {
    if (recoveryTimeoutId) {
      clearTimeout(recoveryTimeoutId)
      recoveryTimeoutId = null
    }
  }

  const showStatusToast = (oldStatus: ConnectionStatus, newStatus: ConnectionStatus) => {
    if (!shouldShowToast() || lastNotifiedStatus === newStatus) return

    const now = Date.now()
    const timeSinceLastToast = now - lastToastTime

    if (timeSinceLastToast < TOAST_COOLDOWN_PERIOD) {
      return
    }

    if (isWorsening(oldStatus, newStatus)) {
      clearRecoveryTimeout()

      if (newStatus === 'OFFLINE') {
        ErrorToast(t('connection_info.toast_offline'), 'wifi_off')
      } else if (newStatus === 'POOR') {
        WarningToast(t('connection_info.toast_poor'), 'android_wifi_4_bar_alert')
      }

      lastNotifiedStatus = newStatus
      lastToastTime = now
      return
    }

    if (isRecovery(oldStatus, newStatus)) {
      clearRecoveryTimeout()

      recoveryTimeoutId = setTimeout(() => {
        if (currentStatus.value === newStatus && shouldShowToast()) {
          const recoveryNow = Date.now()
          const timeSinceLastToastForRecovery = recoveryNow - lastToastTime

          if (timeSinceLastToastForRecovery >= TOAST_COOLDOWN_PERIOD) {
            if (newStatus === 'GOOD') {
              SuccessToast(t('connection_info.toast_recovered') , 'android_wifi_4_bar')
            } else if (newStatus === 'POOR' && lastNotifiedStatus === 'OFFLINE') {
              WarningToast(t('connection_info.toast_improving') , 'android_wifi_4_bar_alert')
            }
            lastNotifiedStatus = newStatus
            lastToastTime = recoveryNow
          }
        }
        recoveryTimeoutId = null
      }, RECOVERY_DELAY)
    }
  }

  watch(
    currentStatus,
    (newStatus, oldStatus) => {
      if (oldStatus === undefined) {
        lastNotifiedStatus = newStatus
        return
      }
      if (newStatus !== oldStatus) {
        showStatusToast(oldStatus, newStatus)
      }
    },
    { immediate: false }
  )

  const scheduleNextCheck = () => {
    const jitter = CHECK_INTERVAL * 0.25
    const delay = Math.floor(CHECK_INTERVAL - jitter + Math.random() * jitter * 2)
    intervalId = setTimeout(async () => {
      await checkConnection()
      if (isMonitoring.value) {
        scheduleNextCheck()
      }
    }, delay)
  }

  const startMonitoring = () => {
    if (isMonitoring.value) return

    isMonitoring.value = true

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    const connection = getNetworkConnection()
    if (connection) {
      connection.addEventListener('change', updateNetworkInfo)
    }

    scheduleNextCheck()
  }

  const stopMonitoring = () => {
    isMonitoring.value = false

    if (intervalId) {
      clearTimeout(intervalId)
      intervalId = null
    }

    clearRecoveryTimeout()

    window.removeEventListener('offline', handleOffline)
    window.removeEventListener('online', handleOnline)

    const connection = getNetworkConnection()
    if (connection) {
      connection.removeEventListener('change', updateNetworkInfo)
    }
  }

  const statusLabel = computed(() => {
    const labels: Record<ConnectionStatus, string> = {
      GOOD: t('connection_info.status_good'),
      POOR: t('connection_info.status_poor'),
      OFFLINE: t('connection_info.status_offline'),
    }
    return labels[currentStatus.value]
  })

  return {
    currentStatus,
    connectionSpeed,
    networkInfo,
    isMonitoring,
    statusLabel,
    startMonitoring,
    stopMonitoring,
    checkConnection,
  }
}
